<template>
  <ScriptGoogleMaps
    v-if="validMarkers.length > 0"
    trigger="visible"
    :class="{
      'mb-6 h-[384px] w-full': true,
    }"
    @ready="onReady"
  >
    <ScriptGoogleMapsMarker
      v-for="(item, index) in eventsWithLocation"
      :key="item.event._id"
      :position="{ lat: item.lat, lng: item.lng }"
      @click="toggleMarkerTooltip(index)"
    >
      <ScriptGoogleMapsOverlayView
        v-if="activeMarkerIndex === index"
        anchor="bottom-center"
        :offset="{ x: 0, y: -20 }"
      >
        <div
          :class="{
            'relative w-64 rounded-lg bg-www-color-white p-4 shadow-lg': true,
          }"
        >
          <button
            type="button"
            aria-label="Close"
            :class="{
              'absolute right-3 top-4 text-lg leading-none text-[#617480]': true,
            }"
            @click="activeMarkerIndex = null"
          >
            &times;
          </button>
          <h4
            :class="{
              'pr-4 font-ptc-raleway text-base font-bold leading-tight text-www-color-black': true,
            }"
          >
            {{ item.event.title }}
          </h4>
          <p
            v-if="item.event.cardTime"
            :class="{
              'mt-1 font-ptc-raleway text-sm text-www-color-neutral-medium-dark': true,
            }"
          >
            {{ item.event.cardTime }}
          </p>
          <p
            v-if="item.event.location || item.event.eventFormat === 'virtual'"
            :class="{
              'text-text-www-color-neutral-medium-dark mt-1 font-ptc-raleway text-sm': true,
            }"
          >
            {{ item.event.location }}
          </p>
          <PTCLink
            v-if="item.event.offSiteUrl || item.event.currentPathname"
            :to="item.event.offSiteUrl ?? item.event.currentPathname ?? ''"
            :class="{
              'mt-2 inline-block font-ptc-raleway text-sm font-bold text-www-color-accent underline': true,
            }"
          >
            View Event
          </PTCLink>
        </div>
      </ScriptGoogleMapsOverlayView>
    </ScriptGoogleMapsMarker>
  </ScriptGoogleMaps>
</template>

<script setup lang="ts">
import PTCLink from '~/components/Atmos/PTCLink/PTCLink.vue';
import { ScriptGoogleMaps, ScriptGoogleMapsMarker, ScriptGoogleMapsOverlayView } from '#components';
const paginationResult = defineProps<EventPaginationResult>();
const eventsWithLocation = computed(() => {
  return paginationResult.items
    .map((event) => ({
      event,
      lat: event.mappedLocation?.location?.lat,
      lng: event.mappedLocation?.location?.lng,
    }))
    .filter(
      (
        item
      ): item is {
        event: (typeof paginationResult.items)[number];
        lat: number;
        lng: number;
      } => item.lat != null && item.lng != null && !isNaN(item.lat) && !isNaN(item.lng)
    );
});
const validMarkers = computed(() => eventsWithLocation.value.map(({ lat, lng }) => ({ lat, lng })));
const { fitMarkers } = useGoogleMap();

const map = shallowRef<GoogleMapInstance | null>(null);
const mapsApi = shallowRef<GoogleMapsApi | null>(null);

// Controls which marker's tooltip is currently active
const activeMarkerIndex = ref<number | null>(null);

function toggleMarkerTooltip(index: number): void {
  activeMarkerIndex.value = activeMarkerIndex.value === index ? null : index;
}

function onReady(payload: GoogleMapsReadyPayload) {
  map.value = unref(payload.map);
  mapsApi.value = unref(payload.mapsApi);

  if (!map.value || !mapsApi.value) return;

  fitMarkers({
    map: map.value,
    mapsApi: mapsApi.value,
    markers: validMarkers.value,
  });
}

watch(
  validMarkers,
  (markers) => {
    if (!map.value || !mapsApi.value) return;

    fitMarkers({
      map: map.value,
      mapsApi: mapsApi.value,
      markers,
    });
  },
  {
    immediate: true,
  }
);
</script>

<style lang="scss" scoped></style>
